import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DynamicForm } from '../../../components/form-builder/form-builder.types';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { RefTypeService } from './ref-type.service';
import { RefType } from '@/types';
import { DrawerComponent } from '../../../components/drawer/drawer.component';
import { DataTableRowActions, DynamicDataTable } from '../../../components/data-table-builder/data-table.types';
import { getRefTypeDataTableStructure } from './utils/ref-type-data-table-object';
import { DrawerObject } from '../../../components/drawer/drawer.types';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { getRefTypeAddDrawerObject } from './utils/ref-type-add-drawer-object';
import { RefTypeRepository } from '../../../store/ref-type-state/ref-type-state.repository';
import { MessageService } from 'primeng/api';
import { getRefTypeFormObject } from './utils/ref-type-form-object';
import { getRefTypeUpdateDrawerObject } from './utils/ref-type-update-drawer-object';
import { DialogComponent } from '../../../components/dialog/dialog.component';
import { DialogObject } from '../../../components/dialog/dialog.types';
import { getRefTypeDeleteObject } from './utils/ref-type-delete-dialog-object';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'sm-ref-type',
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    PageHeaderComponent,
    PageHeaderComponent,
    DrawerComponent,
    DataTableBuilderComponent,
    DialogComponent,
  ],
  templateUrl: './ref-type.component.html',
  styleUrl: './ref-type.component.scss',
})
export class RefTypeComponent implements OnInit {
  //services
  refTypeService = inject(RefTypeService);
  refTypeRepository = inject(RefTypeRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);

  //breadcrumb
  breadcrumb = [
    {
      label: 'settings.refType.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.refType.breadcrumb.refTypes',
      routerLink: ['/settings', 'ref-type'],
    },
  ];

  //drawer
  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  refTypeDrawer?: DrawerObject;

  //dialog
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  refTypeDialog?: DialogObject;

  //form-object
  formObject: DynamicForm = getRefTypeFormObject({ store: this.refTypeRepository });

  //data-table
  //row-actions
  rowActions: DataTableRowActions = {
    editAction: () => {
      this.refTypeDrawer = getRefTypeUpdateDrawerObject({
        store: this.refTypeRepository,
        formObject: this.formObject,
        action: () => this.editRefType(this.refTypeRepository.getNested('dto')),
        toggleVisibility: () => {
          this.drawerComponent.toggleVisible();
        },
      });
      this.drawerComponent.toggleVisible();
    },
    deleteAction: () => {
      this.refTypeDialog = getRefTypeDeleteObject({
        t: this.translate,
        store: this.refTypeRepository,
        representation: this.refTypeRepository.getNested('response', 'id'),
        action: () => {
          this.deleteRefType(this.refTypeRepository.getNested<number>('response', 'id'));
          this.dialogComponent.toggleVisible();
        },
        toggleVisiblity: () => {
          this.dialogComponent.toggleVisible();
        },
      });
      this.dialogComponent.toggleVisible();
    },
  };
  //data
  data: RefType[] = [];
  //data-table-object
  dataTableObject: DynamicDataTable = getRefTypeDataTableStructure({
    store: this.refTypeRepository,
    rowActions: this.rowActions,
    createAction: () => {
      this.refTypeDrawer = getRefTypeAddDrawerObject({
        store: this.refTypeRepository,
        formObject: this.formObject,
        action: () => this.addRefType(),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
  });
  loading = true;

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.refTypeService.getRefTypes().subscribe({
      next: data => {
        this.data = data;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.refType.message.error.loading'),
        });
      },
    });
  }

  addRefType() {
    this.refTypeService.addRefType(this.refTypeRepository.getNested('dto')).subscribe({
      next: () => {
        this.drawerComponent.toggleVisible();
        this.loadData();
        this.refTypeRepository.reset();
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.refType.message.success.added'),
        });
      },
      error: () => {
        this.loading = false;
        this.drawerComponent.toggleVisible();
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.refType.message.error.added'),
        });
      },
    });
  }

  editRefType(refType: RefType) {
    this.refTypeService.updateRefType(refType).subscribe({
      next: () => {
        this.drawerComponent.toggleVisible();
        this.loadData();
        this.refTypeRepository.reset();
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.refType.message.success.edited'),
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.refType.message.error.edited'),
        });
      },
    });
  }

  deleteRefType(id: number) {
    return this.refTypeService.deleteRefType(id).subscribe({
      next: () => {
        this.loadData();
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.refType.message.success.deleted'),
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.refType.message.error.deleted'),
        });
      },
    });
  }
}
